import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { Box, IconButton, Stack, ListItemButton, Tooltip } from '@mui/material';
import { saveAs } from 'file-saver';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { AiFillLinkedin } from 'react-icons/ai';
import { BsBookmark, BsFillBookmarkFill } from 'react-icons/bs';
import { FaWindowClose } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  IJob,
  pinJob,
  removePinJob,
  downloadJobDescription,
  amplitude,
  IBooleanQueryBuilderDTO,
  generateBooleanQuery,
} from 'wisestep-api';
import {
  WsColors,
  WsElementWithPopOver,
  WsIconAssignCandidate,
  WsIconBroadcast,
  WsIconEdit,
  WsIconMoreVer,
  WsIconText,
  WsShareJob,
  WsTypoColors,
  WsTypoVariants,
  WsIconTwitter,
  WsIconClosedJob,
  WsIconPublish,
} from 'wisestep-base';

import { ActivateFeature } from '../../../../../components/ActivateFeature';
import IconTextLink from '../../../../../components/IconTextLink';
import { useWorkspaceContext } from '../../../../../context';
import { reOpenJob } from '../../../../../features/jobs/helper/JobHelperService';
import Google from '../../../../../images/jobBoardsIcons/Google.png';
import InDeed from '../../../../../images/jobBoardsIcons/InDeed.png';
import { careerPageSettingStore } from '../../../../../utils/careerPageSettingsStore';
import { success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import socialMediaShareUrl, { indexJob } from '../../../../../utils/shareUrl';
import { PotentialRevenueDTO } from '../../hooks/useListingJobs';
import CloseJobDialogue from '../closeJobDialogue/CloseJobDialogue';
import { ViewDetailsDrawer } from '../viewDetails';

interface JobCardRightChildProps {
  job: IJob;
  pageRefresh: () => void;
  loggedInUserId: string;
  canManageJob: boolean;
  potentialRevenueData?: PotentialRevenueDTO[];
  isPotentialRevenueVisible?: boolean;
}
export const sharingOfJobs = [
  { text: 'Google (Direct)', icon: <img src={Google} alt={Google} height={18} width={18} /> },
  { text: 'Google (LinkedIn)', icon: <img src={Google} alt={Google} height={18} width={18} /> },
  { text: 'LinkedIn', icon: <AiFillLinkedin color={WsColors.TheBlues500} size={19} /> },
  { text: 'InDeed', icon: <img src={InDeed} alt={InDeed} height={18} width={18} /> },
  {
    text: 'Twitter',
    icon: <WsIconTwitter color={WsColors.TheBlues500} height={19} width={19} />,
  },
];

export const copyStringToClipboard = (data: string) => {
  function handler(event) {
    event.clipboardData.setData('text/plain', data);
    event.preventDefault();
    document.removeEventListener('copy', handler, true);
  }
  document.addEventListener('copy', handler, true);
  document.execCommand('copy');
};

const JobCardRightChild = ({
  job,
  pageRefresh,
  loggedInUserId,
  canManageJob,
}: JobCardRightChildProps) => {
  const isAssigned =
    loggedInUserId && job.assignedTo?.filter((user) => user.recruiterId == loggedInUserId);
  const workspace = useWorkspaceContext();

  const navigate = useNavigate();
  const navigateToPublish = () => navigate(`/jobs/${job.id}/publish`);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openViewDetails, setOpenViewDetails] = useState<boolean>(false);
  const [selectTab, setSelectTab] = useState<string>('JD');
  const pinnedJob = async () => {
    if (job.id) {
      const response = await pinJob(job.id);
      if (response.data) {
        setNotification({
          message: 'The job is added to your favorites list.',
          type: 'success',
        });
        pageRefresh();
      }
    }
  };
  const amplitudeEvents = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'JobListing',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

  const firingJoblistAmplitudeEvent = {
    ['facebook']: () => amplitudeEvents('Job_Listing_job_card_share_fb', 'facebook'),

    ['whatsapp']: () => amplitudeEvents('Job_Listing_job_card_share_whatsapp', 'Whatsapp'),

    ['twitter']: () => amplitudeEvents('Job_Listing_job_card_share_twitter', 'Twitter'),

    ['linkedin']: () => amplitudeEvents('Job_Listing_job_card_share_LinkedIn', 'Linkedin'),

    ['email']: () => amplitudeEvents('Job_Listing_job_card_share_Email', 'Email'),
  };

  const [openCloseDialog, setOpenCloseDialog] = useState<boolean>(false);

  const downloadJD = async () => {
    if (job.id) {
      const downloadDetails = await downloadJobDescription(job.id);
      const headers = downloadDetails.headers;
      const file = new Blob([downloadDetails.data], {
        type: headers['content-type'],
      });
      saveAs(file, headers['content-disposition'].replace('attachment; filename=', ''));
    }
  };
  const handleCloseClick = async () => {
    if (job?.isClosedForAssigningRecruiters) {
      setNotification(await reOpenJob(job?.id ?? ''));
      pageRefresh && pageRefresh();
    } else setOpenCloseDialog(true);
  };

  const unPinnedJob = async () => {
    if (job.id) {
      const response = await removePinJob(job.id);
      if (response.data) {
        setNotification({
          message: 'The job is removed from your favorites list.',
          type: 'success',
        });
        pageRefresh();
      }
    }
  };
  const careerPageSetting = careerPageSettingStore((state) => state.careerPageSettings);
  const indexJobToCareerPage = async (source) => {
    firingJoblistAmplitudeEvent[source]();
    indexJob(job, careerPageSetting);
  };
  const closeViewDetails = () => setOpenViewDetails(false);
  const getLocationForSearch = (location) => {
    const locationParam: string[] = [];
    if (location.city) {
      locationParam.push(location.city);
    }
    if (location.state) {
      locationParam.push(location.state);
    }
    if (location.country) {
      locationParam.push(location.country);
    }
    if (isEmpty(locationParam)) {
      locationParam.push(location.continent);
    }
    return locationParam.join(',');
  };
  const getGeneratedBooleanQuery = async () => {
    const query: IBooleanQueryBuilderDTO = {
      mode: 'quick',
      jobTitle: job.title,
      requiredSkills: job.skills,
      jobDescription: job.description,
      jobId: job.id,
      queryTypes: { open: false, focused: true, comprehensive: false },
      queryResponse: { open: 'false', focused: 'true', comprehensive: 'false' },
    };
    const queryResponse = await generateBooleanQuery(query);
    return queryResponse.data.booleanQuery.focused;
  };

  const handleSharingOfJobsAcrossPlatforms = async (sharingOfJobsPlatform: string) => {
    let booleanstring = '';
    if (job.boolQueryFocused == null) {
      booleanstring = await getGeneratedBooleanQuery();
    } else {
      booleanstring = job.boolQueryFocused;
    }
    switch (sharingOfJobsPlatform) {
      case 'Google (Direct)':
        amplitudeEvents('Job_Listing_job_card_Social_share_Google_direct', 'Google(Direct)');
        window.open(
          'https://www.google.com/search?q=filetype:(pdf OR doc OR docx OR rtf)  ("' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '") (resume OR cv) -job -sample  ' +
            booleanstring
        );
        break;
      case 'Google (LinkedIn)':
        amplitudeEvents('Job_Listing_job_card_Social_share_Google_LinkedIn', 'Google Linkedin ');
        window.open(
          'https://www.google.com/search?q=site:linkedin.com/in ("' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '") ' +
            booleanstring.replaceAll(/[()]/gm, '')
        );
        break;
      case 'LinkedIn':
        amplitudeEvents('Job_Listing_job_card_Social_share_LinkedIn', 'LinkedIn ');
        window.open('https://www.linkedin.com/search/results/people/?keywords=' + booleanstring);
        break;
      case 'InDeed':
        amplitudeEvents('Job_Listing_job_card_Social_share_Indeed', 'Indeed');
        window.open(
          'https://resumes.indeed.com/search?l=' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '&q=' +
            booleanstring
        );
        break;
      case 'Twitter':
        amplitudeEvents('Job_Listing_job_card_Social_share_twitter', 'Twitter ');
        window.open(
          'https://twitter.com/search?f=user&q=near:"' +
            getLocationForSearch(job.locations && job.locations[0]) +
            '" within:100mi ' +
            job.title
        );
        break;
    }
  };

  const handleClipBoard = (data: string) => {
    copyStringToClipboard(data);
    setNotification(success('Copied Successfully !!'));
  };

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Box my={-0.2} mr={-1}>
          <WsIconText
            text="JD"
            onClick={() => {
              amplitudeEvents('Job_Listing_job_card_View_JD', 'View JD');
              setSelectTab('JD');
              setOpenViewDetails(true);
            }}
            icon={<></>}
            textSize={WsTypoVariants.BODY1}
            textColor={WsTypoColors.PRIMARY}
            textCursor="pointer"
          />
        </Box>
        {job.questions && job?.questions?.length > 0 && (
          <Box my={-0.2} mr={-1}>
            <WsIconText
              text="Questions"
              onClick={() => {
                amplitudeEvents('Job_Listing_job_card_View_QUESTIONS', 'View Questions');
                setSelectTab('QUESTIONS');
                setOpenViewDetails(true);
              }}
              icon={<></>}
              textSize={WsTypoVariants.BODY1}
              textColor={WsTypoColors.PRIMARY}
              textCursor="pointer"
            />
          </Box>
        )}
        <Box display="flex-end">
          <Tooltip title={'Clone Job'}>
            <IconButton onClick={() => navigate(`/jobs/${job.id}/clone`)} sx={{ p: 0, mt: -0.5 }}>
              <ContentCopyIcon sx={{ color: WsColors.TertiaryShades500 }} fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {(canManageJob || !job.isClosedForAssigningRecruiters) && (
          <Box>
            <Tooltip title={'Publish Job'}>
              <IconButton
                onClick={() => {
                  navigateToPublish();
                  amplitudeEvents('Job_Listing_job_card_publish', 'Publish');
                }}
                sx={{ p: 0, mt: -0.5 }}
              >
                <WsIconPublish color={WsColors.TertiaryShades500} height={17} width={17} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box mx={1}>
          <Tooltip title="Save">
            <IconButton
              onClick={() => {
                amplitudeEvents('Job_Listing_job_card_Save', 'Save');
                job.isJobPinned ? unPinnedJob() : pinnedJob();
              }}
              sx={{ p: 0, mt: -0.5 }}
            >
              {job.isJobPinned ? (
                <BsFillBookmarkFill color={WsColors.TertiaryShades500} size={17} />
              ) : (
                <BsBookmark size={17} color={WsColors.TertiaryShades500} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Box mx={1}>
          <Tooltip title="Download">
            <IconButton
              sx={{ p: 0, mt: -0.5 }}
              onClick={() => {
                amplitudeEvents('Job_Listing_job_card_Download', 'Download JD ');
                downloadJD();
              }}
              className="download-jd"
            >
              <FileDownloadOutlinedIcon sx={{ color: WsColors.TertiaryShades500, width: 25 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <ActivateFeature feature="JS_SOCIAL_SHARING_OF_JOBS">
          <Tooltip title="Share">
            <WsShareJob
              indexJob={indexJobToCareerPage}
              shareUrl={socialMediaShareUrl(job, careerPageSetting)}
              shareUrlLinkedIn={socialMediaShareUrl(job, careerPageSetting)}
              title={job.title ?? ''}
              isGrey={true}
              iconSize="small"
              clipBoard={handleClipBoard}
            />
          </Tooltip>
          <Box>
            <WsElementWithPopOver
              element={<PersonSearchIcon fontSize="medium" color="secondary" />}
            >
              <Stack p={1}>
                {sharingOfJobs?.map((sharingPlatform) => (
                  <ListItemButton
                    key={sharingPlatform.text}
                    onClick={() => handleSharingOfJobsAcrossPlatforms(sharingPlatform.text)}
                  >
                    <WsIconText
                      text={sharingPlatform.text}
                      icon={sharingPlatform.icon}
                      textSize={WsTypoVariants.BODY1}
                      textColor={WsTypoColors.PRIMARY}
                      textCursor="pointer"
                    />
                  </ListItemButton>
                ))}
              </Stack>
            </WsElementWithPopOver>
          </Box>
        </ActivateFeature>
        <WsElementWithPopOver element={<WsIconMoreVer />}>
          <Stack p={1}>
            {(job.canManageJob || job.createdBy === loggedInUserId || isEmpty(isAssigned)) && (
              <>
                <ListItemButton>
                  <IconTextLink
                    text="Edit Job"
                    link={`/jobs/${job.id}/edit`}
                    icon={<WsIconEdit />}
                  />
                </ListItemButton>
                <ListItemButton onClick={handleCloseClick}>
                  <IconTextLink
                    text={job?.isClosedForAssigningRecruiters ? 'Reopen Job' : 'Close Job'}
                    icon={
                      job?.isClosedForAssigningRecruiters ? (
                        <WsIconClosedJob color={WsColors.TertiaryShades800} />
                      ) : (
                        <FaWindowClose color={WsColors.TertiaryShades800} />
                      )
                    }
                  />
                </ListItemButton>
                <ListItemButton>
                  <IconTextLink
                    text="Assign Recruiters"
                    link={`/jobs/${job.id}/assign`}
                    icon={<WsIconAssignCandidate />}
                  />
                </ListItemButton>
              </>
            )}
            <ListItemButton>
              <IconTextLink
                text="Broadcast Job to Vendors"
                link={`/jobs/${job.id}/assign`}
                icon={<WsIconBroadcast />}
                disabled={true}
              />
            </ListItemButton>
          </Stack>
        </WsElementWithPopOver>
      </Stack>
      {openCloseDialog && (
        <CloseJobDialogue
          onClose={() => setOpenCloseDialog(false)}
          open={openCloseDialog}
          job={job}
          pageRefresh={pageRefresh}
        />
      )}
      <ViewDetailsDrawer
        job={job}
        selectTab={selectTab == 'JD' ? 0 : 1}
        open={openViewDetails}
        closeDetails={closeViewDetails}
      />
    </>
  );
};

export default JobCardRightChild;
